import { useEffect, useRef } from 'react';
import type { PlayerAction } from '../playerReducer';

export function useMediaEvents(
  mediaEl: HTMLMediaElement | null,
  dispatch: React.Dispatch<PlayerAction>,
  callbacks: {
    onTimeUpdate?: (time: number) => void;
    onPlay?: () => void;
    onPause?: () => void;
    onEnded?: () => void;
  },
) {
  // Track whether media is currently playing so we can skip the
  // browser's low-frequency timeupdate dispatch during playback (the
  // RAF-based useFrameAccurateTime hook handles it at frame precision).
  const isPlayingRef = useRef(false);

  useEffect(() => {
    if (!mediaEl) return;

    const onTimeUpdate = () => {
      // During playback the RAF loop already dispatches TIME_UPDATE at
      // frame-level precision.  Skipping the browser timeupdate here
      // avoids redundant state updates and re-renders.
      if (!isPlayingRef.current) {
        dispatch({ type: 'TIME_UPDATE', currentTime: mediaEl.currentTime });
      }
      callbacks.onTimeUpdate?.(mediaEl.currentTime);
    };

    const onPlay = () => {
      isPlayingRef.current = true;
      dispatch({ type: 'PLAY' });
      callbacks.onPlay?.();
    };

    const onPause = () => {
      isPlayingRef.current = false;
      dispatch({ type: 'PAUSE' });
      callbacks.onPause?.();
    };

    const onLoadedMetadata = () => {
      dispatch({ type: 'DURATION_SET', duration: mediaEl.duration });
      dispatch({ type: 'MEDIA_READY' });
    };

    const onDurationChange = () => {
      dispatch({ type: 'DURATION_SET', duration: mediaEl.duration });
    };

    const onProgress = () => {
      if (mediaEl.buffered.length > 0) {
        dispatch({
          type: 'BUFFER_UPDATE',
          bufferedEnd: mediaEl.buffered.end(mediaEl.buffered.length - 1),
        });
      }
    };

    const onWaiting = () => dispatch({ type: 'SET_BUFFERING', isBuffering: true });
    const onCanPlay = () => dispatch({ type: 'SET_BUFFERING', isBuffering: false });
    const onEnded = () => {
      isPlayingRef.current = false;
      // Snap the playhead to the very end so the UI shows the full duration.
      dispatch({ type: 'TIME_UPDATE', currentTime: mediaEl.duration });
      dispatch({ type: 'MEDIA_ENDED' });
      // The browser's native loop prevents the 'ended' event, so this
      // only fires when looping is off and playback genuinely ended.
      callbacks.onEnded?.();
    };

    const onVolumeChange = () => {
      dispatch({ type: 'SET_VOLUME', volume: mediaEl.volume });
    };

    const onRateChange = () => {
      dispatch({ type: 'SET_PLAYBACK_RATE', rate: mediaEl.playbackRate });
    };

    mediaEl.addEventListener('timeupdate', onTimeUpdate);
    mediaEl.addEventListener('play', onPlay);
    mediaEl.addEventListener('pause', onPause);
    mediaEl.addEventListener('loadedmetadata', onLoadedMetadata);
    mediaEl.addEventListener('durationchange', onDurationChange);
    mediaEl.addEventListener('progress', onProgress);
    mediaEl.addEventListener('waiting', onWaiting);
    mediaEl.addEventListener('canplay', onCanPlay);
    mediaEl.addEventListener('ended', onEnded);
    mediaEl.addEventListener('volumechange', onVolumeChange);
    mediaEl.addEventListener('ratechange', onRateChange);

    // If metadata already loaded
    if (mediaEl.readyState >= 1) {
      dispatch({ type: 'DURATION_SET', duration: mediaEl.duration });
      dispatch({ type: 'MEDIA_READY' });
    }

    return () => {
      mediaEl.removeEventListener('timeupdate', onTimeUpdate);
      mediaEl.removeEventListener('play', onPlay);
      mediaEl.removeEventListener('pause', onPause);
      mediaEl.removeEventListener('loadedmetadata', onLoadedMetadata);
      mediaEl.removeEventListener('durationchange', onDurationChange);
      mediaEl.removeEventListener('progress', onProgress);
      mediaEl.removeEventListener('waiting', onWaiting);
      mediaEl.removeEventListener('canplay', onCanPlay);
      mediaEl.removeEventListener('ended', onEnded);
      mediaEl.removeEventListener('volumechange', onVolumeChange);
      mediaEl.removeEventListener('ratechange', onRateChange);
    };
  }, [mediaEl, dispatch, callbacks]);
}
